import { useState } from 'react';
import { X } from 'lucide-react';
import { Link } from 'react-router-dom';
import Button from '../shared/Button';
import { useEffect, useMemo, useCallback } from 'react';
import { DocumentService } from '../../services/DocumentService';
import { Toast } from '../shared/Toast';
import Select from '../shared/Select';
import TextFieldWithCounter from '../shared/TextFieldWithCounter';
import { isValidDocumentTitle, isValidTitleLength } from '../../utils/validators';
import Tooltip from '../shared/Tooltip';
import { InfoTooltip } from '../shared/InfoTooltip';
import { useSubscription } from '../../hooks/useSubscription';
import { isDocumentTypeAllowedForPlan } from '../../utils/documentTypePlanUtils';
import {
	getDocumentTypeDescription,
	getDocumentTypeDisplayName
} from '../../utils/documentTypeDescriptions';
import LimitReachedModal, { isLimitReachedError } from './LimitReachedModal';

const styles = {
	section: 'fixed inset-0 z-[9999] flex items-center justify-center p-4',
	backDrop: 'absolute inset-0 bg-black/70',
	modal: 'relative z-10 w-full max-w-[500px] rounded-[18px] bg-white shadow-2xl p-[30px] max-h-[90vh] overflow-y-auto no-scrollbar',
	heading: 'text-25 font-bold text-dark-blue mb-1 leading-100',
	subHeading: 'text-gray text-sm xl:mb-6 md:mb-2',
	label: 'text-base leading-100 font-medium text-dark-blue',
	note: 'bg-lightPurple p-4 rounded-xl text-sm leading-21 text-dark-blue font-medium italic xl:mb-8 mb-3 flex flex-col',
	span: 'font-semibold text-base leading-100 text-purple xl:mb-2.5 mb-1'
};

export default function CreateDocumentModal({ isOpen, onClose, isImported, importedFile, onDocumentCreated }) {
	const { getEffectivePlanName } = useSubscription();
	// Updated props
	const [documentTitle, setDocumentTitle] = useState('');
	const [formats, setFormats] = useState([]);
	const [documentTypes, setDocumentTypes] = useState([]);
	const [formatId, setFormatId] = useState('');
	const [documentTypeId, setDocumentTypeId] = useState('');
	// const [universities, setUniversities] = useState([]);
	// const [universityId, setUniversityId] = useState('');
	// const [isOthers, setIsOthers] = useState(false);
	const [loadingFormats, setLoadingFormats] = useState(false);
	const [loadingDocumentTypes, setLoadingDocumentTypes] = useState(false);
	// const [loadingUniversities, setLoadingUniversities] = useState(false);
	// Removed inline error state; using Toast for errors only
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [isCloseConfirmOpen, setIsCloseConfirmOpen] = useState(false);
	const [limitModalOpen, setLimitModalOpen] = useState(false);
	const [limitPayload, setLimitPayload] = useState(null);

	const resetModalState = () => {
		setDocumentTitle('');
		setFormatId('');
		setDocumentTypeId('');
		setDocumentTypes([]);
		setFormats([]);
		// setUniversityId('');
		// setUniversities([]);
		// setLoadingUniversities(false);
		setLoadingFormats(false);
		setLoadingDocumentTypes(false);
		setIsSubmitting(false);
		// setIsOthers(false);
	};

	const handleClose = () => {
		resetModalState();
		if (typeof onClose === 'function') onClose();
	};

	const handleRequestClose = () => {
		setIsCloseConfirmOpen(true);
	};

	const handleConfirmDiscard = () => {
		setIsCloseConfirmOpen(false);
		handleClose();
	};

	const handleCancelDiscard = () => {
		setIsCloseConfirmOpen(false);
	};

	// const handleBackdropClick = (e) => {
	// 	if (e.target === e.currentTarget) handleClose();
	// };

	const handleCreateDocument = async () => {
		try {
			setIsSubmitting(true);

			// Document title validation
			if (!documentTitle.trim()) {
				Toast.error('Please enter your document title.');
				setIsSubmitting(false);
				return;
			}
			if (!isValidTitleLength(documentTitle)) {
				Toast.error('Document title must be between 3 and 75 characters.');
				setIsSubmitting(false);
				return;
			}
			if (!isValidDocumentTitle(documentTitle)) {
				Toast.error('Document title can only include letters');
				setIsSubmitting(false);
				return;
			}

			const payload = {
				title: documentTitle || 'Untitled Document',
				format: formatId,
				documentType: documentTypeId,
				university: 'others',
				status: 'Draft'
			};
			const result = isImported && importedFile
				? await DocumentService.importDocument(importedFile, payload)
				: await DocumentService.createDocument(payload);
			if (!result || (result.status && result.status !== 200)) {
				if (isLimitReachedError(result)) {
					setLimitPayload(result);
					setLimitModalOpen(true);
					return;
				}
				const message = result?.message || 'Failed to create document';
				Toast.error(message);
				return;
			}
			// Notify rest of the app that documents list has changed
			try {
				window.dispatchEvent(new Event('documents:updated'));
			} catch { }

			Toast.success('Your document is ready. Start writing, add sections, or insert citations as you go.');

			handleClose();
			if (typeof onDocumentCreated === 'function') {
				const newId = result?.document?._id || result?._id;
				onDocumentCreated(newId);
			}
		} catch (e) {
			if (isLimitReachedError(e)) {
				setLimitPayload(e?.response?.data || e);
				setLimitModalOpen(true);
				return;
			}
			const message =
				e?.response?.data?.message ||
				e?.response?.data?.error ||
				e?.message ||
				'Failed to create document';
			Toast.error(message);
		} finally {
			setIsSubmitting(false);
		}
	};

	// Load document types (fresh each time modal opens)
	const loadDocumentTypes = useCallback(async () => {
		try {
			setLoadingDocumentTypes(true);
			const data = await DocumentService.fetchDocumentTypes();
			const apiDocumentTypes = Array.isArray(data?.documentTypes)
				? data.documentTypes.sort((a, b) => (a.name || '').localeCompare(b.name || ''))
				: [];
			setDocumentTypes(apiDocumentTypes);
		} catch (e) {
			console.error('Failed to load document types:', e);
			Toast.error('Failed to load document types. Check that you are logged in and the API is running.');
			setDocumentTypes([]);
		} finally {
			setLoadingDocumentTypes(false);
		}
	}, []);

	// Load formats when modal opens
	const loadFormats = useCallback(async () => {
		try {
			setLoadingFormats(true);
			const data = await DocumentService.fetchFormats();
			const apiFormats = Array.isArray(data?.formats) ? data.formats : [];
			setFormats(apiFormats);
		} catch (e) {
			console.error('Failed to load formats:', e);
			Toast.error('Failed to load formatting styles.');
			setFormats([]);
		} finally {
			setLoadingFormats(false);
		}
	}, []);

	// Reload catalog every time the modal opens (admin may add types while app is open)
	useEffect(() => {
		if (!isOpen) return;
		Promise.all([loadDocumentTypes(), loadFormats()]).catch((error) => {
			console.error('Failed to load modal data:', error);
		});
	}, [isOpen, loadDocumentTypes, loadFormats]);

	// Effect to prevent body scrolling when modal is open
	useEffect(() => {
		if (isOpen) {
			document.body.style.overflow = 'hidden';
		} else {
			document.body.style.overflow = '';
		}

		// Cleanup: Re-enable scrolling when the component unmounts or modal closes
		return () => {
			document.body.style.overflow = '';
		};
	}, [isOpen]);

	// Derive formats from selected document type (if needed for filtering)
	const availableFormats = useMemo(() => {
		// For now, return all formats since we're using separate services
		// In the future, you might want to filter formats based on document type
		return formats;
	}, [formats]);

	// If there's only a single available format (e.g., APA 7th edition),
	// auto-select it while keeping the dropdown visible (but disabled).
	useEffect(() => {
		if (availableFormats.length === 1 && !formatId) {
			setFormatId(availableFormats[0]._id);
		}
	}, [availableFormats, formatId]);

	// Only show document types allowed for the user's plan (minTier from API)
	const effectivePlan = getEffectivePlanName();
	const documentTypeOptions = useMemo(() => {
		return documentTypes
			.filter((t) => isDocumentTypeAllowedForPlan(effectivePlan, t))
			.map((t) => {
				const id = t._id || t.id;
				return {
					value: id,
					label: getDocumentTypeDisplayName(t) || t.name,
					description: getDocumentTypeDescription(t)
				};
			});
	}, [documentTypes, effectivePlan]);

	const selectedDocumentTypeDescription = useMemo(() => {
		if (!documentTypeId) return '';
		const match = documentTypes.find((t) => (t._id || t.id) === documentTypeId);
		return match ? getDocumentTypeDescription(match) : '';
	}, [documentTypeId, documentTypes]);

	const hasTypesAbovePlan = useMemo(
		() => documentTypes.some((t) => !isDocumentTypeAllowedForPlan(effectivePlan, t)),
		[documentTypes, effectivePlan]
	);

	useEffect(() => {
		if (!documentTypeId || documentTypeOptions.length === 0) return;
		const stillValid = documentTypeOptions.some((o) => o.value === documentTypeId);
		if (!stillValid) setDocumentTypeId('');
	}, [documentTypeId, documentTypeOptions]);

	// When both format and document type are selected, fetch universities
	// useEffect(() => {
	// 	async function loadUniversities() {
	// 		if (!formatId || !documentTypeId) return;
	// 		try {
	// 			setLoadingUniversities(true);
	// 			const data = await DocumentService.searchUniversities({ formatId, documentTypeId });
	// 			const list = Array.isArray(data?.universities) ? data.universities : [];
	// 			// Ensure "Others" is always present as a selectable option
	// 			const hasOthers = list.some((u) => u?._id === 'others');
	// 			const withOthers = hasOthers ? list : [...list, { _id: 'others', name: 'Others' }];
	// 			setUniversities(withOthers);
	// 		} catch (e) {
	// 			Toast.error('Failed to load universities');
	// 		} finally {
	// 			setLoadingUniversities(false);
	// 		}
	// 	}
	// 	loadUniversities();
	// }, [formatId, documentTypeId]);

	if (!isOpen) return null;

	return (
		<div className={styles.section}>
			{/* Backdrop */}
			<div className={styles.backDrop} />

			{/* Modal */}
			<div className={styles.modal}>
				{isImported && (
					<div className="w-14 h-7 p-2 rounded-[8px] bg-[#F2F3F7] mb-3">
						<p className="text-xs font-medium text-dark-blue">Step 2</p>
					</div>
				)}
				<button
					onClick={handleRequestClose}
					className="absolute top-8 right-2 md:right-6 w-5 h-5 bg-dark-blue/40 hover:bg-dark-blue/80 rounded-full flex items-center justify-center transition-colors"
				>
					<X className="w-3 h-3 text-white" />
				</button>

				{isCloseConfirmOpen && (
					<div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/70 px-4">
						<div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-[0_18px_45px_rgba(7,16,52,0.18)]">
							<div className="mb-4 flex items-start gap-3">
								<div className="mt-1 flex h-9 w-9 items-center justify-center rounded-full bg-[#FFF4E5]">
									<span className="text-base font-semibold text-[#D97706]">!</span>
								</div>
								<div>
									<h3 className="mb-1 text-[18px] font-semibold leading-6 text-dark-blue">
										Discard this document setup?
									</h3>
									<p className="text-sm leading-5 text-gray">
										If you close now, we&apos;ll discard this setup and you&apos;ll need to start again.
									</p>
								</div>
							</div>
							<div className="mt-6 flex items-center justify-end gap-3">
								<button
									type="button"
									onClick={handleCancelDiscard}
									className="inline-flex min-w-[110px] items-center justify-center rounded-[10px] border border-lightGrey px-4 py-2 text-sm font-medium text-dark-blue hover:bg-[#F7F7FB] transition-colors"
								>
									Keep editing
								</button>
								<Button
									variant="primary"
									className="inline-flex min-w-[120px] items-center justify-center rounded-[10px] px-4 py-2 text-sm font-semibold !w-auto"
									onClick={handleConfirmDiscard}
								>
									Discard setup
								</Button>
							</div>
						</div>
					</div>
				)}

				{/* Heading */}
				<div className="flex items-center gap-1 sm:gap-2">
					<h2 className={styles.heading}>Create New Document</h2>
					<Tooltip
						placement="bottom"
						content="Start a fresh paper, thesis, or dissertation. WriteForm will build the complete APA-style structure for you."
					>
						<InfoTooltip />
					</Tooltip>
				</div>
				<p className={styles.subHeading}>Begin your document — we&apos;ll build the structure for you.</p>

				<div className="h-[1px] bg-[#F0F0F0] xl:mb-[25px] mb-2 mx-[-30px]" />
				{/* Input fields */}
				<div className="grid grid-cols-1 gap-6 xl:mb-4 mb-2">
					{/* Document Title */}
					<div>
						<div className="flex items-center gap-2 xl:mb-3 mb-1">
							<label htmlFor="document-title" className={styles.label}>
								Document title <span className="text-red-500">*</span>
							</label>
							<Tooltip content="Enter the title of your paper. You can edit this later if it changes.">
								<InfoTooltip />
							</Tooltip>
						</div>
						<TextFieldWithCounter
							className='focus:outline-none focus:border-purple'
							id="document-title"
							placeholder="Enter Document Name"
							value={documentTitle}
							onChange={(e) => setDocumentTitle(e.target.value)}
							minLength={3}
							maxLength={75}
						/>
					</div>

					{/* Document Type */}
					<div>
						<div className="flex items-center gap-2 xl:mb-3 mb-1">
							<label htmlFor="document-type" className={styles.label}>
								Document type <span className="text-red-500">*</span>
							</label>
							<Tooltip content="Select whether you're writing a paper, thesis, or dissertation. This ensures the correct structure is built for you.">
								<InfoTooltip />
							</Tooltip>
						</div>
						<Select
							id="document-type"
							value={documentTypeId}
							onChange={(e) => {
								setDocumentTypeId(e.target.value);
							}}
							placeholder="Select type"
							options={documentTypeOptions}
							loading={loadingDocumentTypes}
							disabled={loadingDocumentTypes}
						/>
						{selectedDocumentTypeDescription ? (
							<p className="text-xs text-gray mt-1.5 leading-relaxed">{selectedDocumentTypeDescription}</p>
						) : null}
						{effectivePlan !== 'laureate' && hasTypesAbovePlan && (
								<p className="text-xs text-gray mt-1.5">
									Want theses, dissertations, or professional manuscripts?{' '}
									<Link to="/subscription" className="text-purple font-medium hover:underline" onClick={handleClose}>
										Upgrade your plan
									</Link>
								</p>
							)}
					</div>

					{/* Formatting Style */}
					<div>
						<div className="flex items-center gap-2 xl:mb-3 mb-1">
							<label htmlFor="formatting-style" className={styles.label}>
								Formatting style <span className="text-red-500">*</span>
							</label>
							<Tooltip content="WriteForm applies APA 7th edition rules automatically. More styles coming soon.">
								<InfoTooltip />
							</Tooltip>
						</div>
						<Select
							id="formatting-style"
							value={formatId}
							onChange={(e) => {
								setFormatId(e.target.value); /* setUniversityId(''); setUniversities([]); */
							}}
							placeholder="Select style"
							options={availableFormats.map((f) => ({ value: f._id, label: f.name }))}
							loading={loadingFormats}
							disabled={loadingFormats || availableFormats.length === 1}
						/>
					</div>

					{/* University */}
					{/* <div>
						<div className="flex items-center gap-2 xl:mb-3 mb-1">
							<label htmlFor="university" className={styles.label}>
								University <span className="text-red-500">*</span>
							</label>
							<InfoTooltip />
						</div>
				<Select
					id="university"
					value={universityId}
					onChange={(e) => { const val = e.target.value; setUniversityId(val); setIsOthers(val === 'others'); }}
					placeholder="Select university"
					options={universities.map((u) => ({ value: u._id, label: u.name }))}
					loading={loadingUniversities}
					disabled={!formatId || !documentTypeId || loadingUniversities}
				/>
					</div> */}
				</div>

				{/* Important note */}
				<div className={styles.note}>
					<span className={styles.span}>Important note:</span> WriteForm follows public style guidelines (e.g., APA 7th Edition). We are not affiliated with or endorsed by the American Psychological Association.
				</div>

				<div className="h-[1px] bg-[#F0F0F0] xl:mb-[25px] mb-2 mx-[-30px]" />

				{/* Buttons */}
				<div className="flex xl:gap-3 gap-2">
					<Button
						onClick={handleCreateDocument}
						disabled={isSubmitting || !documentTitle || !formatId || !documentTypeId}
					>
						{isSubmitting ? 'Creating…' : 'Create Document'}
					</Button>
				</div>
			</div>

			<LimitReachedModal
				isOpen={limitModalOpen}
				onClose={() => {
					setLimitModalOpen(false);
					setLimitPayload(null);
				}}
				limitPayload={limitPayload}
				onSuccess={() => {
					Toast.success('Allowances reset. You can create another document now.');
				}}
			/>
		</div>
	);
}
